import { useMemo, useState } from 'react';
import { Badge, Button, Checkbox, EmptyState, IconPhoto } from '@indipia/ui-core';
import type { PhotoShare, SharedPhoto } from '../api/customerHome';
import { CustomerImg } from './PreviewContext';
import { fmtDateDow, parseDtm } from './format';

interface Group {
  /** 공유 묶음 ID — 묶음 정보가 없는 사진(방어)은 'ungrouped' */
  key: string;
  share: PhotoShare | null;
  /** 묶음 정보가 없을 때 헤더에 쓸 날짜(그 묶음 첫 사진의 공유 시각) */
  fallbackDtm: string;
  photos: { photo: SharedPhoto; flatIndex: number; isNew: boolean }[];
  hasNew: boolean;
}

const UNGROUPED = 'ungrouped';

/**
 * 공유 묶음(shares 순서 = 최신 발송 먼저)별 그룹. flatIndex는 뷰어가 탭 전체를 연속으로 넘길 때의 위치.
 * 사진은 묶음 안에서 촬영 시각 최신순. 어느 묶음에도 속하지 않은 사진은 맨 뒤 "묶음 없음" 그룹으로 모은다(서버가 항상 bundleId를 주지만 방어).
 */
export function groupPhotos(
  photos: SharedPhoto[],
  shares: PhotoShare[],
  lastVisitedDtm: string | null,
): { groups: Group[]; ordered: SharedPhoto[] } {
  const since = lastVisitedDtm ? parseDtm(lastVisitedDtm).getTime() : null;
  const known = new Set(shares.map((s) => s.bundleId));
  const byBundle = new Map<string, SharedPhoto[]>();
  for (const p of photos) {
    const key = known.has(p.bundleId) ? p.bundleId : UNGROUPED;
    byBundle.set(key, [...(byBundle.get(key) ?? []), p]);
  }
  const ordered: SharedPhoto[] = [];
  const build = (key: string, share: PhotoShare | null): Group | null => {
    const list = (byBundle.get(key) ?? []).slice().sort((a, b) => (b.takenAt ?? '').localeCompare(a.takenAt ?? ''));
    if (list.length === 0) return null;
    const items = list.map((photo) => {
      const flatIndex = ordered.length;
      ordered.push(photo);
      return { photo, flatIndex, isNew: since !== null && parseDtm(photo.sharedDtm).getTime() > since };
    });
    return { key, share, fallbackDtm: list[0].sharedDtm, photos: items, hasNew: items.some((i) => i.isNew) };
  };
  const groups = [...shares.map((s) => build(s.bundleId, s)), build(UNGROUPED, null)].filter((g): g is Group => g !== null);
  return { groups, ordered };
}

function DownloadIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

interface Props {
  photos: SharedPhoto[];
  shares: PhotoShare[];
  lastVisitedDtm: string | null;
  onOpen: (flatIndex: number) => void;
  /**
   * 사진 저장(zip) 요청 — 선택한 공유 묶음 ID 목록, 생략(undefined)이면 전체.
   * 주지 않으면(미리보기) 저장 버튼·선택 체크박스를 그리지 않는다.
   */
  onDownloadZip?: (bundleIds?: string[]) => void;
}

/**
 * 공유사진 탭 — 공유 묶음별 그룹. 묶음 헤더에 제목·공유일·발송자, 메시지가 있으면 본문 카드를 보인다.
 * 새 사진이 든 묶음 헤더에 NEW, 새 썸네일에는 파란 테두리·우상단 점을 표시한다(탭을 열어도 이번 방문 동안 유지).
 * 저장: 묶음 체크박스로 고른 묶음만("선택 사진 저장") 또는 전체를 서버 zip 하나로 내려받는다.
 */
export function PhotoTab({ photos, shares, lastVisitedDtm, onOpen, onDownloadZip }: Props) {
  const { groups } = useMemo(() => groupPhotos(photos, shares, lastVisitedDtm), [photos, shares, lastVisitedDtm]);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());

  if (groups.length === 0) {
    return <EmptyState icon={<IconPhoto />} title="공유된 사진이 없습니다" description="사장님이 사진을 공유하면 여기에 표시됩니다." />;
  }

  const selectable = groups.filter((g) => g.key !== UNGROUPED);
  const selectedBundleIds = selectable.filter((g) => selected.has(g.key)).map((g) => g.key);
  const selectedCount = selectable.filter((g) => selected.has(g.key)).reduce((sum, g) => sum + g.photos.length, 0);
  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <div className="flex flex-col gap-05">
      {onDownloadZip && (
        <div className="flex gap-03">
          <Button size="sm" variant="primary" disabled={selectedBundleIds.length === 0} onClick={() => onDownloadZip(selectedBundleIds)}>
            <DownloadIcon /> 선택 사진 저장{selectedCount > 0 ? ` (${selectedCount}장)` : ''}
          </Button>
          <Button size="sm" variant="secondary" onClick={() => onDownloadZip()}>
            <DownloadIcon /> 전체 사진 저장
          </Button>
        </div>
      )}
      {groups.map((g) => {
        const title = g.share?.title?.trim() || '공유 사진';
        const dateLabel = fmtDateDow(g.share?.sentDtm ?? g.fallbackDtm);
        const sender = g.share?.senderName?.trim();
        const message = g.share?.message?.trim();
        return (
          <section key={g.key}>
            <div className="mb-03 flex items-center gap-03">
              {onDownloadZip && g.key !== UNGROUPED && (
                <Checkbox bare aria-label={`${title} 선택`} checked={selected.has(g.key)} onChange={() => toggle(g.key)} />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-body-sm font-bold text-text">{title}</div>
                <div className="text-caption text-text-secondary">
                  {dateLabel} 공유{sender ? ` · ${sender} 사장님` : ''}
                </div>
              </div>
              {g.hasNew && (
                <Badge status="accent">
                  NEW
                </Badge>
              )}
              <span className="text-caption font-bold text-text-secondary">{g.photos.length}장</span>
            </div>
            {message && (
              <div className="mb-03 whitespace-pre-line rounded-md border border-border bg-surface-raised p-04 text-body-sm text-text">
                {sender && <div className="mb-02 text-caption text-text-secondary">{sender} 사장님의 메시지</div>}
                {message}
              </div>
            )}
            <div className="grid grid-cols-3 gap-03">
              {g.photos.map(({ photo, flatIndex, isNew }) => {
                const thumb = photo.thumbUrl ?? photo.originalUrl;
                return (
                  <button
                    key={photo.photoId}
                    type="button"
                    aria-label={`${title} 사진 크게 보기`}
                    onClick={() => onOpen(flatIndex)}
                    className={['relative aspect-square overflow-hidden rounded-sm bg-surface active:scale-95', isNew ? 'ring-2 ring-info' : ''].join(' ')}
                  >
                    {thumb && <CustomerImg src={thumb} alt="" loading="lazy" draggable={false} className="h-full w-full object-cover" />}
                    {isNew && <span aria-hidden="true" className="absolute right-02 top-02 h-03 w-03 rounded-full bg-info" />}
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
